import { useState } from "react";
import { BarChart3, Cookie, Database, Megaphone, ShieldCheck, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { usePrivacyConsent } from "../../contexts/PrivacyConsentContext";
import type { ConsentPreferences } from "../../legal/legal.types";
import { DataControlCard } from "./DataControlCard";
import { PrivacyTrustNote } from "./PrivacyTrustNote";

export function PrivacySettingsPanel() {
  const { preferences, saveConsent, clearLocalDrafts, clearConsentAndPreferences } =
    usePrivacyConsent();

  const [draft, setDraft] = useState<ConsentPreferences>(preferences);

  const updateDraft = (key: keyof ConsentPreferences, value: boolean) => {
    if (key === "essential") return;

    setDraft((prev) => ({
      ...prev,
      essential: true,
      [key]: value,
    }));
  };

  const save = () => {
    saveConsent(draft);
    toast.success("Privacy preferences saved");
  };

  const clearDrafts = () => {
    clearLocalDrafts();
    toast.success("Local drafts cleared from this device");
  };

  const resetConsent = () => {
    clearConsentAndPreferences();
    toast.success("Privacy choices reset");
  };

  return (
    <div className="space-y-5">
      <PrivacyTrustNote variant="settings" />

      <div className="grid gap-4 md:grid-cols-2">
        <DataControlCard
          icon={ShieldCheck}
          title="Essential storage"
          description="Required for login, authentication, private links, payment continuity, security, encrypted experience delivery, and fraud prevention. This cannot be turned off inside LRNSY because the platform would not function safely without it."
          action={<StatusPill label="Always active" />}
        />

        <DataControlCard
          icon={Cookie}
          title="Functional storage"
          description="Keeps LRNSY smoother by preserving local drafts, UI preferences, unfinished experiences, remembered archetypes, and device-local continuity."
          action={
            <Toggle
              checked={draft.functional}
              onChange={(checked) => updateDraft("functional", checked)}
            />
          }
        />

        <DataControlCard
          icon={BarChart3}
          title="Analytics"
          description="Helps LRNSY understand product reliability, loading performance, drop-offs, payment completion, recipient completion, replay behavior, and conversion health. Private letter text is not used for advertising profiling."
          action={
            <Toggle
              checked={draft.analytics}
              onChange={(checked) => updateDraft("analytics", checked)}
            />
          }
        />

        <DataControlCard
          icon={Megaphone}
          title="Marketing attribution"
          description="Allows limited campaign and pixel measurement so LRNSY can understand whether campaigns are working. Private emotional messages are not sent to ad platforms for profiling."
          action={
            <Toggle
              checked={draft.marketing}
              onChange={(checked) => updateDraft("marketing", checked)}
            />
          }
        />

        <DataControlCard
          icon={Database}
          title="Local drafts"
          description="Clear unfinished local experience drafts stored on this device. This does not delete paid experiences or account records from LRNSY servers."
          action={
            <button
              type="button"
              onClick={clearDrafts}
              className="rounded-2xl border border-white/10 px-4 py-3 text-[10px] font-black uppercase tracking-[0.22em] text-white/65 transition hover:bg-white/10 hover:text-white"
            >
              Clear local drafts
            </button>
          }
        />

        <DataControlCard
          icon={Trash2}
          title="Reset privacy choices"
          description="Clear your saved cookie and privacy choices on this device. LRNSY will ask again the next time you visit."
          action={
            <button
              type="button"
              onClick={resetConsent}
              className="rounded-2xl border border-red-300/20 bg-red-400/[0.06] px-4 py-3 text-[10px] font-black uppercase tracking-[0.22em] text-red-100/80 transition hover:bg-red-400/[0.12]"
            >
              Reset choices
            </button>
          }
        />
      </div>

      <div className="sticky bottom-4 rounded-[24px] border border-white/10 bg-black/70 p-4 shadow-[0_20px_80px_rgba(0,0,0,0.55)] backdrop-blur-2xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-5 text-white/45">
            Changes are saved to this browser and affect optional analytics and marketing tools.
          </p>

          <button
            type="button"
            onClick={save}
            className="rounded-2xl bg-white px-5 py-3 text-[10px] font-black uppercase tracking-[0.22em] text-black transition hover:bg-amber-100"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-100/80">
      {label}
    </span>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative h-8 w-14 rounded-full border transition ${
        checked ? "border-amber-200/30 bg-amber-200/30" : "border-white/10 bg-white/10"
      }`}
      aria-pressed={checked}
    >
      <span
        className={`absolute top-1 h-6 w-6 rounded-full bg-white transition ${
          checked ? "left-7" : "left-1"
        }`}
      />
    </button>
  );
}
